/**
 * Weather Intelligence Service for PaperSedge
 * 
 * Integrates with OpenWeatherMap API to provide weather-aware
 * activity suggestions and environmental context.
 */

class WeatherService {
  constructor() {
    this.apiKey = import.meta.env.VITE_OPENWEATHER_API_KEY;
    this.baseUrl = 'https://api.openweathermap.org/data/2.5';
    this.cache = new Map();
    this.cacheTimeout = 15 * 60 * 1000; // 15 minutes
    
    // Debug log for API key availability
    console.log('🌤️ Weather Service initialized - API Key available:', !!this.apiKey);
    if (!this.apiKey) {
      console.warn('🌤️ VITE_OPENWEATHER_API_KEY not found in environment variables');
    }
  }

  /**
   * Get current weather for a location
   */
  async getCurrentWeather(city = null) {
    // Use provided city, or get from user settings, or default to Raleigh
    const location = city || this.getDefaultLocation();
    const cacheKey = `current-${location}`;
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    try {
      if (!this.apiKey) {
        console.warn('OpenWeather API key not configured, using mock data');
        return this.getMockWeatherData();
      }

      const response = await fetch(
        `${this.baseUrl}/weather?q=${location}&appid=${this.apiKey}&units=imperial`
      );

      if (!response.ok) {
        throw new Error(`Weather API error: ${response.status}`);
      }

      const data = await response.json();
      const weatherData = this.parseWeatherData(data);
      
      // Cache the result
      this.cache.set(cacheKey, {
        data: weatherData,
        timestamp: Date.now()
      });

      return weatherData;
    } catch (error) {
      console.error('Error fetching weather data:', error);
      return this.getMockWeatherData();
    }
  }

  /**
   * Get weather forecast for the next few days
   */
  async getWeatherForecast(city = null, days = 5) {
    const location = city || this.getDefaultLocation();
    const cacheKey = `forecast-${location}-${days}`;
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    try {
      if (!this.apiKey) {
        console.warn('OpenWeather API key not configured, using mock data');
        return this.getMockForecastData();
      }

      const response = await fetch(
        `${this.baseUrl}/forecast?q=${city}&appid=${this.apiKey}&units=imperial&cnt=${days * 8}`
      );

      if (!response.ok) {
        throw new Error(`Weather API error: ${response.status}`);
      }

      const data = await response.json();
      const forecastData = this.parseForecastData(data);
      
      // Cache the result
      this.cache.set(cacheKey, {
        data: forecastData,
        timestamp: Date.now()
      });

      return forecastData;
    } catch (error) {
      console.error('Error fetching forecast data:', error);
      return this.getMockForecastData();
    }
  }

  /**
   * Parse weather API response into standardized format
   */
  parseWeatherData(apiData) {
    return {
      temperature: Math.round(apiData.main.temp),
      feelsLike: Math.round(apiData.main.feels_like),
      humidity: apiData.main.humidity,
      conditions: apiData.weather[0].main.toLowerCase(),
      description: apiData.weather[0].description,
      icon: apiData.weather[0].icon,
      windSpeed: Math.round(apiData.wind?.speed || 0),
      visibility: apiData.visibility ? Math.round(apiData.visibility / 1609) : null, // Convert to miles
      uvIndex: null, // Not available in basic API
      timestamp: Date.now(),
      location: `${apiData.name}, ${apiData.sys.country}`
    };
  }

  /**
   * Parse forecast API response
   */
  parseForecastData(apiData) {
    const dailyForecasts = {};
    
    apiData.list.forEach(item => {
      const date = new Date(item.dt * 1000);
      const dateKey = date.toISOString().split('T')[0];
      
      if (!dailyForecasts[dateKey]) {
        dailyForecasts[dateKey] = {
          date: dateKey,
          high: item.main.temp_max,
          low: item.main.temp_min,
          conditions: item.weather[0].main.toLowerCase(),
          description: item.weather[0].description,
          icon: item.weather[0].icon,
          precipitation: item.pop * 100, // Convert to percentage
          hours: []
        };
      } else {
        dailyForecasts[dateKey].high = Math.max(dailyForecasts[dateKey].high, item.main.temp_max);
        dailyForecasts[dateKey].low = Math.min(dailyForecasts[dateKey].low, item.main.temp_min);
      }
      
      dailyForecasts[dateKey].hours.push({
        time: date.getHours(),
        temp: Math.round(item.main.temp),
        conditions: item.weather[0].main.toLowerCase(),
        precipitation: item.pop * 100
      });
    });

    return Object.values(dailyForecasts).map(day => ({
      ...day,
      high: Math.round(day.high),
      low: Math.round(day.low)
    }));
  }

  /**
   * Generate weather-aware activity suggestions
   */
  generateWeatherSuggestions(weatherData, familyContext = {}) {
    const suggestions = [];
    const { temperature, conditions, description } = weatherData;
    const { temporal = {} } = familyContext;

    // Perfect weather suggestions
    if (conditions === 'clear' && temperature >= 65 && temperature <= 80) {
      suggestions.push({
        type: 'weather-optimal',
        priority: 'high',
        title: 'Perfect Weather for Outdoor Activities',
        description: `${temperature}°F and ${description} - ideal for family time outside`,
        action: 'suggest-outdoor-activity',
        timing: 'optimal',
        reasoning: 'Weather conditions are perfect for outdoor family activities',
        confidence: 0.9,
        weatherContext: { temperature, conditions, description }
      });
    }

    // Rainy day indoor suggestions
    if (conditions === 'rain' || conditions === 'drizzle') {
      suggestions.push({
        type: 'weather-indoor',
        priority: 'medium',
        title: 'Rainy Day Indoor Activities',
        description: 'Perfect weather for cozy indoor family time',
        action: 'suggest-indoor-activity',
        timing: 'good',
        reasoning: 'Rain makes outdoor activities less enjoyable',
        confidence: 0.8,
        weatherContext: { temperature, conditions, description }
      });
    }

    // Hot weather warnings
    if (temperature > 85) {
      suggestions.push({
        type: 'weather-caution',
        priority: 'medium',
        title: 'Hot Weather Advisory',
        description: `${temperature}°F - plan indoor activities or early morning/evening outdoor time`,
        action: 'adjust-activity-timing',
        timing: 'important',
        reasoning: 'High temperatures can be uncomfortable for extended outdoor activities',
        confidence: 0.85,
        weatherContext: { temperature, conditions, description }
      });
    }

    // Cold weather suggestions
    if (temperature < 45) {
      suggestions.push({
        type: 'weather-cold',
        priority: 'low',
        title: 'Bundle Up Weather',
        description: `${temperature}°F - dress warmly for outdoor activities`,
        action: 'prepare-warm-clothes',
        timing: 'good',
        reasoning: 'Cold weather requires extra preparation for outdoor activities',
        confidence: 0.7,
        weatherContext: { temperature, conditions, description }
      });
    }

    // Weekend-specific suggestions
    const now = new Date();
    const isWeekend = now.getDay() === 0 || now.getDay() === 6;
    
    if (isWeekend && temperature >= 60 && temperature <= 85 && conditions !== 'rain') {
      suggestions.push({
        type: 'weather-weekend',
        priority: 'medium',
        title: 'Great Weekend Weather',
        description: `${temperature}°F - perfect for family outings and activities`,
        action: 'plan-weekend-activity',
        timing: 'optimal',
        reasoning: 'Weekend with good weather is ideal for family activities',
        confidence: 0.85,
        weatherContext: { temperature, conditions, description }
      });
    }

    return suggestions;
  }

  /**
   * Assess outdoor activity suitability
   */
  assessOutdoorSuitability(weatherData) {
    const { temperature, conditions, windSpeed, humidity } = weatherData;
    let score = 100;
    let factors = [];

    // Temperature assessment
    if (temperature < 32) {
      score -= 40;
      factors.push('Very cold temperature');
    } else if (temperature < 50) {
      score -= 20;
      factors.push('Cold temperature');
    } else if (temperature > 90) {
      score -= 30;
      factors.push('Very hot temperature');
    } else if (temperature > 85) {
      score -= 15;
      factors.push('Hot temperature');
    }

    // Weather condition assessment
    if (conditions === 'thunderstorm') {
      score -= 60;
      factors.push('Thunderstorms');
    } else if (conditions === 'rain') {
      score -= 40;
      factors.push('Rain');
    } else if (conditions === 'drizzle') {
      score -= 20;
      factors.push('Light rain');
    } else if (conditions === 'snow') {
      score -= 30;
      factors.push('Snow');
    } else if (conditions === 'fog' || conditions === 'mist') {
      score -= 15;
      factors.push('Poor visibility');
    }

    // Wind assessment
    if (windSpeed > 20) {
      score -= 25;
      factors.push('Very windy');
    } else if (windSpeed > 15) {
      score -= 10;
      factors.push('Windy');
    }

    // Humidity assessment (for hot days)
    if (temperature > 80 && humidity > 70) {
      score -= 15;
      factors.push('High humidity');
    }

    // Normalize score
    score = Math.max(0, Math.min(100, score));

    return {
      score,
      rating: this.getOutdoorRating(score),
      factors,
      recommendation: this.getOutdoorRecommendation(score, weatherData)
    };
  }

  /**
   * Get outdoor activity rating based on score
   */
  getOutdoorRating(score) {
    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    if (score >= 40) return 'fair';
    if (score >= 20) return 'poor';
    return 'unsuitable';
  }

  /**
   * Get outdoor activity recommendation
   */
  getOutdoorRecommendation(score, weatherData) {
    const { temperature, conditions } = weatherData;
    
    if (score >= 80) {
      return 'Perfect weather for any outdoor activities!';
    } else if (score >= 60) {
      return 'Good weather for most outdoor activities.';
    } else if (score >= 40) {
      if (temperature < 50) {
        return 'Dress warmly, but outdoor activities are still enjoyable.';
      } else if (temperature > 85) {
        return 'Stay hydrated and seek shade during outdoor activities.';
      } else {
        return 'Outdoor activities possible with some preparation.';
      }
    } else if (score >= 20) {
      return 'Consider indoor alternatives or postpone outdoor plans.';
    } else {
      return 'Best to stay indoors and wait for better weather.';
    }
  }

  /**
   * Mock weather data for development/fallback
   */
  getMockWeatherData() {
    const mockConditions = ['clear', 'clouds', 'rain'];
    const mockTemp = 65 + Math.random() * 20; // 65-85°F
    const condition = mockConditions[Math.floor(Math.random() * mockConditions.length)];
    
    return {
      temperature: Math.round(mockTemp),
      feelsLike: Math.round(mockTemp + (Math.random() - 0.5) * 4),
      humidity: Math.round(40 + Math.random() * 40),
      conditions: condition,
      description: condition === 'clear' ? 'clear sky' : condition === 'clouds' ? 'scattered clouds' : 'light rain',
      icon: condition === 'clear' ? '01d' : condition === 'clouds' ? '02d' : '10d',
      windSpeed: Math.round(Math.random() * 10),
      visibility: 10,
      timestamp: Date.now(),
      location: 'Raleigh, NC (Mock Data)'
    };
  }

  /**
   * Mock forecast data for development/fallback
   */
  getMockForecastData() {
    const forecasts = [];
    const today = new Date();
    
    for (let i = 0; i < 5; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      
      const baseTemp = 70 + (Math.random() - 0.5) * 20;
      const conditions = ['clear', 'clouds', 'rain'][Math.floor(Math.random() * 3)];
      
      forecasts.push({
        date: date.toISOString().split('T')[0],
        high: Math.round(baseTemp + 5),
        low: Math.round(baseTemp - 5),
        conditions,
        description: conditions === 'clear' ? 'sunny' : conditions === 'clouds' ? 'partly cloudy' : 'light rain',
        icon: '01d',
        precipitation: conditions === 'rain' ? Math.round(Math.random() * 80) : Math.round(Math.random() * 20),
        hours: []
      });
    }
    
    return forecasts;
  }

  /**
   * Get default weather location from settings or fallback
   */
  getDefaultLocation() {
    // Check localStorage for user preference
    const savedLocation = localStorage.getItem('weather-location');
    if (savedLocation) {
      return savedLocation;
    }
    
    // Default fallback
    return 'Raleigh,NC,US';
  }

  /**
   * Set user's preferred weather location
   */
  setLocation(location) {
    localStorage.setItem('weather-location', location);
    // Clear cache to force refresh with new location
    this.clearCache();
  }

  /**
   * Clear weather cache (useful for testing)
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get coordinates from zip code for packing lists
   */
  async getCoordinatesFromZip(zipCode, countryCode = 'US') {
    try {
      if (!this.apiKey) {
        console.warn('OpenWeather API key not configured');
        return null;
      }

      console.log(`🌤️ Getting coordinates for zip: ${zipCode},${countryCode}`);
      const url = `${this.baseUrl}/weather?zip=${zipCode},${countryCode}&appid=${this.apiKey}`;
      console.log(`🌤️ API URL: ${url.replace(this.apiKey, '[API_KEY]')}`);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        console.error(`🌤️ Weather API error: ${response.status} - ${response.statusText}`);
        const errorText = await response.text();
        console.error(`🌤️ Error details:`, errorText);
        throw new Error(`Weather API error: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`🌤️ Got coordinates for ${zipCode}:`, data);
      
      return {
        lat: data.coord.lat,
        lon: data.coord.lon,
        cityName: data.name,
        country: data.sys.country
      };
    } catch (error) {
      console.error(`🌤️ Error getting coordinates from zip ${zipCode}:`, error);
      return null;
    }
  }

  /**
   * Get weather forecast for trip dates using zip code
   */
  async getWeatherForTrip(zipCode, startDate, endDate, countryCode = 'US') {
    try {
      console.log(`🌤️ Getting weather for trip: ${zipCode}, ${startDate} to ${endDate}`);
      
      // Get coordinates from zip code
      const coords = await this.getCoordinatesFromZip(zipCode, countryCode);
      if (!coords) {
        console.log(`🌤️ Failed to get coordinates for ${zipCode}, using mock data`);
        return this.getMockTripWeather(startDate, endDate);
      }

      console.log(`🌤️ Got coordinates for ${zipCode}:`, coords);

      const { lat, lon, cityName } = coords;
      const tripStart = new Date(startDate);
      const tripEnd = new Date(endDate);
      const today = new Date();
      
      // Check if trip is within forecast range (5 days)
      const daysUntilTrip = Math.ceil((tripStart - today) / (1000 * 60 * 60 * 24));
      
      if (daysUntilTrip <= 5 && daysUntilTrip >= 0) {
        // Trip is within forecast range - get forecast
        const response = await fetch(
          `${this.baseUrl}/forecast?lat=${lat}&lon=${lon}&appid=${this.apiKey}&units=imperial`
        );
        
        if (!response.ok) {
          throw new Error(`Weather API error: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Filter forecast for trip dates
        const tripForecast = data.list.filter(item => {
          const forecastDate = new Date(item.dt * 1000);
          return forecastDate >= tripStart && forecastDate <= tripEnd;
        }).map(item => ({
          date: new Date(item.dt * 1000).toISOString().split('T')[0],
          temp: Math.round(item.main.temp),
          tempMax: Math.round(item.main.temp_max),
          tempMin: Math.round(item.main.temp_min),
          condition: item.weather[0].main.toLowerCase(),
          description: item.weather[0].description,
          humidity: item.main.humidity,
          windSpeed: Math.round(item.wind?.speed || 0),
          precipitation: Math.round((item.pop || 0) * 100)
        }));
        
        return {
          cityName,
          coordinates: { lat, lon },
          forecast: tripForecast,
          isCurrentForecast: true,
          daysUntilTrip
        };
      } else {
        // Trip is too far out - get current weather as reference
        const response = await fetch(
          `${this.baseUrl}/weather?lat=${lat}&lon=${lon}&appid=${this.apiKey}&units=imperial`
        );
        
        if (!response.ok) {
          throw new Error(`Weather API error: ${response.status}`);
        }
        
        const data = await response.json();
        const currentWeather = {
          temp: Math.round(data.main.temp),
          condition: data.weather[0].main.toLowerCase(),
          description: data.weather[0].description,
          humidity: data.main.humidity,
          windSpeed: Math.round(data.wind?.speed || 0)
        };
        
        return {
          cityName,
          coordinates: { lat, lon },
          currentWeather,
          isCurrentForecast: false,
          daysUntilTrip,
          message: daysUntilTrip > 5 
            ? `Trip is ${daysUntilTrip} days away. Showing current weather as reference.`
            : `Trip was ${Math.abs(daysUntilTrip)} days ago.`
        };
      }
    } catch (error) {
      console.error('Error getting weather for trip:', error);
      return this.getMockTripWeather(startDate, endDate);
    }
  }

  /**
   * Convert trip weather data to packing suggestions
   */
  getTripPackingSuggestions(weatherData) {
    const suggestions = {
      temperature: 'mild',
      weatherCategory: 'mild',
      clothing: [],
      accessories: [],
      notes: [],
      avgTemp: null,
      tempRange: null
    };

    if (weatherData.forecast && weatherData.forecast.length > 0) {
      // Use forecast data
      const temps = weatherData.forecast.map(day => day.temp);
      const maxTemp = Math.max(...temps);
      const minTemp = Math.min(...temps);
      const avgTemp = Math.round(temps.reduce((sum, temp) => sum + temp, 0) / temps.length);
      
      suggestions.avgTemp = avgTemp;
      suggestions.tempRange = `${minTemp}°F - ${maxTemp}°F`;
      
      // Determine temperature category for packing list
      if (avgTemp >= 80) {
        suggestions.temperature = 'hot';
        suggestions.weatherCategory = 'hot';
      } else if (avgTemp >= 65) {
        suggestions.temperature = 'warm';
        suggestions.weatherCategory = 'warm';
      } else if (avgTemp >= 45) {
        suggestions.temperature = 'mild';
        suggestions.weatherCategory = 'mild';
      } else {
        suggestions.temperature = 'cold';
        suggestions.weatherCategory = 'cold';
      }

      // Check for rain
      const hasRain = weatherData.forecast.some(day => 
        day.condition.includes('rain') || 
        day.condition.includes('storm') ||
        day.precipitation > 50
      );
      
      if (hasRain) {
        suggestions.weatherCategory = 'rainy';
        suggestions.notes.push('Rain likely during trip');
      }

      // Temperature variation
      if (maxTemp - minTemp > 20) {
        suggestions.notes.push('Large temperature variation - pack layers');
      }

      // Add weather context
      suggestions.notes.push(`Forecast: ${suggestions.tempRange}, ${weatherData.cityName}`);

    } else if (weatherData.currentWeather) {
      // Use current weather as reference
      const temp = weatherData.currentWeather.temp;
      suggestions.avgTemp = temp;
      
      if (temp >= 80) suggestions.temperature = 'hot';
      else if (temp >= 65) suggestions.temperature = 'warm';
      else if (temp >= 45) suggestions.temperature = 'mild';
      else suggestions.temperature = 'cold';
      
      suggestions.weatherCategory = suggestions.temperature;
      suggestions.notes.push(weatherData.message || 'Based on current weather');
      suggestions.notes.push(`Current: ${temp}°F, ${weatherData.cityName}`);
    }

    return suggestions;
  }

  /**
   * Mock trip weather data for fallback
   */
  getMockTripWeather(startDate, endDate) {
    const mockTemp = 70;
    return {
      cityName: 'Location (Mock Data)',
      currentWeather: {
        temp: mockTemp,
        condition: 'clear',
        description: 'clear sky',
        humidity: 50,
        windSpeed: 5
      },
      isCurrentForecast: false,
      message: 'Using mock weather data - configure VITE_OPENWEATHER_API_KEY for real weather'
    };
  }
}

// Export singleton instance
export const weatherService = new WeatherService();
export default weatherService;